import { NavLink, Route, Routes } from "react-router-dom";
import { useRecoilValue } from "recoil";
import { Icon, IconName, Main, Paper, Span } from "@goodfill/ui-components";
import { BankInformationSection } from "./bankInformation/BankInformationSection";
import { RegulatoryInformationSection } from "./regulatorInformation/RegulatoryInformationSection";
import { TransferSection } from "./transferFunds/transferFunds";
import { TradingPermissionsSection } from "./tradingPermissions/TradingPermissionsSection";
import { PersonalInformationSection } from "./personalInformation/PersonalInformationSection";
import { FinancialInformationSection } from "./financialInformation/financialInformation";
import { FormsAndApplicationsSection } from "./formsAndApplications/FormsAndApplicationsSection";
import { route } from "../../../routes";
import { userCustomerApplicationState } from "../../../state";
import { ApplicationStatusSection } from "./applicationStatus/ApplicationStatusSection";

const sectionKeys = [
  "personalInformation",
  "financialInformation",
  "tradingPermissions",
  "bankInformation",
  "regulatoryInformation",
  "transfer",
  "forms",
  "applicationStatus",
] as const;
type ProfileSectionKey = typeof sectionKeys[number];
interface ProfileSection {
  iconName: IconName;
  label: string;
  link: () => string;
}
type SectionRecord = Record<ProfileSectionKey, ProfileSection>;

const profileSections: SectionRecord = {
  personalInformation: {
    iconName: "user",
    label: "Personal Information",
    link: route.personalInformation,
  },
  financialInformation: {
    iconName: "paper-dollar",
    label: "Financial Information",
    link: route.financialInformation,
  },
  tradingPermissions: {
    iconName: "paper",
    label: "Trading Permissions",
    link: route.tradingPermissions,
  },
  bankInformation: {
    iconName: "bank",
    label: "Bank Information",
    link: route.bankInformation,
  },
  regulatoryInformation: {
    iconName: "copybook",
    label: "Regulatory Information",
    link: route.regulatoryInformation,
  },
  transfer: {
    iconName: "transfer",
    label: "Transfer Funds",
    link: route.transfer,
  },
  forms: {
    iconName: "document-plus",
    label: "Forms & applications",
    link: route.forms,
  },
  applicationStatus: {
    iconName: "saved",
    label: "Application Status",
    link: route.applicationStatus,
  },
};

export const AccountInfo: React.FC = () => {
  const userCustomerApplicationData = useRecoilValue(userCustomerApplicationState);

  if (userCustomerApplicationData === null) {
    return (
      <div className="flex w-96">
        <div className="flex flex-col my-1 md:my-2 mx-4 md:mx-8 px-4 md:px-8 gap-4 md:gap-8">
          <div className="flex flex-col w-96">
            <div className="flex items-center justify-between my-2 md:my-4">
              <Span font="semibold" size="lg">
                User application not filed yet.
              </Span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const customerApplication = userCustomerApplicationData.submission;

  return (
    <Main>
      <div className="flex my-4 md:my-8">
        <Paper neutral={true}>
          <div className="flex gap-2 md:gap-4 py-4 md:py-8">
            <div className="flex flex-col border-r-2 border-neutral-105 gap-4 md:gap-8 px-4 md:px-8 py-4 md:py-8">
              {Object.entries(profileSections).map(([key, { iconName, label, link }]) => (
                <NavLink to={link()} key={key}>
                  {({ isActive }) => (
                    <div className="flex items-center gap-2 md:gap-4">
                      <Span primary={isActive}>
                        <Icon name={iconName} size={18} />
                      </Span>
                      <Span data-testid={key} font="medium" selectNone={true} primary={isActive}>
                        {label}
                      </Span>
                    </div>
                  )}
                </NavLink>
              ))}
            </div>

            <Routes>
              <Route
                path="personalInformation"
                element={<PersonalInformationSection personalInformation={customerApplication?.personalInformation} />}
              />
              <Route
                path="financialInformation"
                element={
                  <FinancialInformationSection financialInformation={customerApplication?.financialInformation} />
                }
              />
              <Route path="forms" element={<FormsAndApplicationsSection />} />
              <Route path="bankInformation" element={<BankInformationSection />} />
              <Route
                path="regulatoryInformation"
                element={
                  <RegulatoryInformationSection regulatoryInformation={customerApplication.regulatoryInformation} />
                }
              />
              <Route path="transfer" element={<TransferSection />} />
              <Route
                path="tradingPermissions"
                element={<TradingPermissionsSection accountInformation={customerApplication.accountInformation} />}
              />
              <Route path={"applicationStatus"} element={<ApplicationStatusSection />} />
            </Routes>
          </div>
        </Paper>
      </div>
    </Main>
  );
};
